import React, { useEffect, useCallback, useState } from 'react';
import { Button, Form, Input, Modal, message, Segmented } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import './styleNewModalOwner.css';
import { useAuthStore } from '../../../../store/auth/auth';
import { createOwner } from '../../../../api/owners/createOwner';

interface NewOwnerModalProps {
    open: boolean;
    onOk: () => void;
    onCancel: () => void;
}

const NewOwnerModal: React.FC<NewOwnerModalProps> = ({ open, onOk, onCancel }) => {
    const { user, SmartDVRToken } = useAuthStore();
    const [form] = Form.useForm();
    const [ownerType, setOwnerType] = useState<'employee' | 'car'>('employee');
    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            form.setFieldsValue({ name: '' });
            setOwnerType('employee');
            setLoading(false);
        }
    }, [open, form]);

    const handleOk = useCallback(() => {
        if (loading) return;

        setLoading(true);
        form.validateFields().then(values => {
            if (user) {
                const ownerData = {
                    name: values.name,
                    type: ownerType,
                    deviceUID: '',
                    car: ownerType === 'car',
                    description: '',
                };
                createOwner(SmartDVRToken, user.login, ownerData)
                    .then((response) => {
                        if (response?.success) {
                            messageApi.success('Владелец успешно создан!');
                            onOk();
                        } else {
                            messageApi.error(`Ошибка при создании владельца: ${response?.error?.error || 'Неизвестная ошибка'}`);
                            setLoading(false);
                        }
                    })
                    .catch(() => {
                        messageApi.error('Ошибка при создании владельца');
                        setLoading(false);
                    });
            }
        }).catch(() => {
            setLoading(false);
        });
    }, [form, user, SmartDVRToken, onOk, ownerType, loading]);

    const handleCancel = useCallback(() => {
        if (loading) return;
        form.resetFields();
        onCancel();
    }, [form, onCancel, loading]);

    const handleOwnerTypeChange = (value: string | number) => {
        setOwnerType(value as 'employee' | 'car');
    };

    return (
        <>
            {contextHolder}
            <Modal
                title={
                    <div className="titleModal_Group">
                        <span className="spanGroup">Добавить владельца</span>
                        <CloseOutlined onClick={handleCancel} className="closeBut_Group" style={{ pointerEvents: loading ? 'none' : 'auto' }} />
                    </div>
                }
                className="newOwner_modal"
                open={open}
                onOk={handleOk}
                onCancel={handleCancel}
                width={720}
                closable={false}
                confirmLoading={loading}
                maskClosable={!loading}
                keyboard={!loading}
                footer={
                    <div style={{ textAlign: 'center' }}>
                        <Button className="buttonAdd" onClick={handleOk} disabled={loading}>
                            Сохранить
                        </Button>
                    </div>
                }
            >
                <Form form={form} disabled={loading}>
                    <Form.Item
                        className="form_Group"
                        label={<span className="inputLabel_Group">
                            {ownerType === 'employee' ? 'ФИО' : 'Гос. номер'}
                        </span>}
                        name="name"
                        required={false}
                        rules={[{ required: true, message: ownerType === 'employee' ? 'Введите ФИО сотрудника' : 'Введите гос. номер машины' }]}
                    >
                        <Input className="input_Group" />
                    </Form.Item>
                    <Form.Item
                        className="form_Group"
                        label={<span className="inputLabel_Group">Тип</span>}
                        colon={true}
                        style={{ marginBottom: 0 }}
                    >
                        <div className="owner-type-segmented">
                            <Segmented
                                value={ownerType}
                                onChange={handleOwnerTypeChange}
                                options={[
                                    { label: 'сотрудник', value: 'employee' },
                                    { label: 'машина', value: 'car' }
                                ]}
                                className="owner-type-segmented-control"
                                size="middle"
                                disabled={loading}
                            />
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default NewOwnerModal; 